/**
 * SidebarContext — shared mobile-drawer state for the dashboard shell.
 *
 * The desktop rail expands/collapses purely on hover (state lives inside the
 * <Sidebar> component itself), so the only cross-component state that needs
 * to be shared is the mobile drawer's open/closed flag — the dashboard header
 * hamburger lives in a different subtree from the sidebar.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type SidebarContextValue = {
  /** Mobile drawer visibility. */
  mobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
};

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const value = useMemo<SidebarContextValue>(
    () => ({ mobileOpen, openMobile, closeMobile }),
    [mobileOpen, openMobile, closeMobile],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within SidebarProvider');
  return ctx;
}
